import React, { Component } from "react";
import { AppCard, PageLayout } from "../../components/ui";
import _ from "lodash";
import { Column } from "primereact/column";
import { dateBodyTemplate } from "../../utils/columnTemplates";
import { kycSubmissionApiService } from "../../services/api/Kyc/KycSubmissionApiService";
import { KycSubmission } from "../../models/KycSubmission";
import { Button } from "primereact/button";
import KycIndividualModal from "./KycIndividualModal";
import saveAs from "file-saver";
import { fileService } from "../../services/FileService";
import { SearchRequestBaseModel, SortOrderType } from "../../models/SearchRequestModel";
import ExtendedDataTable from "../../components/ExtendedDataTable";
import { dateFilterTemplate, numberFilterMatchModeOptions, stringFilterMatchModeOptions } from "../Paginator";
import { PagedResult } from "../../models/Result";
import { utils } from "../../utils/utils";
import JSZip from "jszip";
import { Download } from "lucide-react";
import { toastService } from "../../services/toastService";

interface PendingKycSubmissonsProp {
    showKycSubmissionsStatus: string;
}

interface PendingKycSubmissonsState {
    isLoading: boolean;
    kycSubmissions: KycSubmission[];
    viewKycSubmissionModal: boolean;
    selectedSubmission: KycSubmission | null;
    showKycSubmissionsStatus: string;
    totalRecords: number;
}

export default class UnderReviewKycSubmissions extends Component<PendingKycSubmissonsProp, PendingKycSubmissonsState> {
    private dataTable: ExtendedDataTable | null = null;

    constructor(props: any | Readonly<any>) {
        super(props);

        this.state = {
            isLoading: false,
            kycSubmissions: [],
            viewKycSubmissionModal: false,
            selectedSubmission: null,
            totalRecords: 0,
            showKycSubmissionsStatus: this.props.showKycSubmissionsStatus,
        };
    }

    loadSubmissionUnderReviewData = async (searchRequest: SearchRequestBaseModel) => {
        this.setState({ isLoading: true });
        searchRequest.filters = utils.manageDataTableFilters(searchRequest.filters);

        let submissionResult: PagedResult<KycSubmission[]> | null;
        submissionResult = null;
        if (this.state.showKycSubmissionsStatus === "UnderReview") submissionResult = await kycSubmissionApiService.getAllUnderReview(searchRequest);
        if (this.state.showKycSubmissionsStatus === "AwaitingApproval")
            submissionResult = await kycSubmissionApiService.getAllAwaitingApproval(searchRequest);

        this.setState({ isLoading: false });

        if (!submissionResult || !submissionResult.success) {
            toastService.error(submissionResult?.message ?? "An error occurred");
        } else this.setState({ kycSubmissions: submissionResult?.data, totalRecords: submissionResult?.totalCount });
    };

    onActionButtonClick = (row: KycSubmission) => {
        this.setState({ viewKycSubmissionModal: true, selectedSubmission: row });
    };

    actionBodyTemplate = (row: KycSubmission) => {
        return <Button label="Review" type="button" severity="warning" onClick={() => this.onActionButtonClick(row)}></Button>;
    };

    continueReviewTemplate = (row: KycSubmission) => {
        return <Button label="Review" type="button" severity="warning" onClick={() => this.startReviewing(row)}></Button>;
    };

    toggleKycSubmissionModalView = () => {
        this.setState({ viewKycSubmissionModal: false });
        this.loadSubmissionUnderReviewData({
            pageNumber: this.dataTable?.state.page!,
            pageSize: this.dataTable?.state.pageRowCount!,
            filters: [],
            sortings: [
                {
                    sortBy: "submissionTimeStamp",
                    sortOrder: 2,
                },
            ],
        });
    };

    startReviewing = async (row: KycSubmission) => {
        this.setState({ viewKycSubmissionModal: true, selectedSubmission: row });
    };

    downloadDocuments = async (submissionId: number) => {
        this.setState({ isLoading: true });

        const documentsResult = await kycSubmissionApiService.getDocuments(submissionId);

        if (!documentsResult.success) {
            this.setState({ isLoading: false });
            toastService.error(documentsResult.message);
            return;
        }

        const zip = new JSZip();
        const photoZip = zip.folder("documents");

        const data = documentsResult.data;
        for (let i = 0; i < data.length; i++) {
            const result = data[i];
            await this.onDownloadFromFileStorage(result.fileStorageId, result.fileName, photoZip);
        }
        /// waits for "await DownloadFileFromS3, then executes the save as which saves the zipped folder created in "DownloadFileFromS3"
        const content = await zip.generateAsync({ type: "blob" });
        saveAs(content, "documents");

        this.setState({ isLoading: false });
    };

    onDownloadFromFileStorage = async (fileStorageId: string, secondaryLabel: string, photoZip: any): Promise<void> => {
        const imagePath = await fileService.GetFile(fileStorageId);

        if (!imagePath.success) {
            this.setState({ isLoading: false });
            toastService.error(imagePath.message);
            return;
        }

        photoZip.file(secondaryLabel, imagePath.data, { base64: true });
    };

    render() {
        return (
            <PageLayout noTitle>
                {this.state.viewKycSubmissionModal && (
                    <KycIndividualModal onHide={this.toggleKycSubmissionModalView} submission={this.state.selectedSubmission!} />
                )}
                <AppCard className="!mt-0">
                    <ExtendedDataTable
                        value={this.state.kycSubmissions}
                        totalRecords={this.state.totalRecords}
                        onChange={this.loadSubmissionUnderReviewData}
                        loading={this.state.isLoading}
                        ref={(ref) => (this.dataTable = ref)}
                        defaultSortOrder={SortOrderType.Descending}
                        defaultSortField="submissionTimeStamp"
                    >
                        <Column {...({ alwaysVisible: true } as any)} field="id" header="Submission ID" filterMatchModeOptions={numberFilterMatchModeOptions}></Column>
                        <Column
                            {...({ alwaysVisible: true } as any)}
                            header="End User First Name"
                            field="endUserFirstName"
                            body={(rowData: KycSubmission) => <span>{rowData.endUserFirstName}</span>}
                            filterMatchModeOptions={stringFilterMatchModeOptions}
                        ></Column>
                        <Column
                            {...({ alwaysVisible: true } as any)}
                            header="End User Last Name"
                            field="endUserLastName"
                            body={(rowData: KycSubmission) => <span>{rowData.endUserLastName}</span>}
                            filterMatchModeOptions={stringFilterMatchModeOptions}
                        ></Column>

                        <Column
                            field="merchantName"
                            header="Merchant Name"
                            sortable
                            filterMatchModeOptions={stringFilterMatchModeOptions}
                        ></Column>
                        <Column
                            field="endUserPhone"
                            header="End User Phone"
                            sortable
                            filterMatchModeOptions={stringFilterMatchModeOptions}
                        ></Column>
                        <Column
                            field="endUserEmail"
                            header="End User Email"
                            sortable
                            filterMatchModeOptions={stringFilterMatchModeOptions}
                        ></Column>
                        <Column field="reason" header="Risk reason" filterMatchModeOptions={stringFilterMatchModeOptions}></Column>
                        <Column
                            field="submissionTimeStamp"
                            header="Submission TimeStamp"
                            body={dateBodyTemplate("submissionTimeStamp")}
                            filterMatchModeOptions={numberFilterMatchModeOptions}
                            dataType="date"
                            filterElement={dateFilterTemplate}
                        ></Column>
                        <Column
                            body={(rowData: KycSubmission) => (
                                <button
                                    type="button"
                                    disabled={rowData.documentsCount === 0}
                                    onClick={(e) => { e.stopPropagation(); this.downloadDocuments(rowData.id); }}
                                    className="inline-flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-medium rounded-md text-primary-600 hover:bg-primary-50 dark:text-primary-400 dark:hover:bg-primary-900/20 transition-colors disabled:opacity-40 disabled:pointer-events-none"
                                    title="Download documents"
                                >
                                    <Download size={14} />
                                    <span>Download</span>
                                </button>
                            )}
                            header="Uploaded Files"
                        ></Column>
                        <Column field="reviewerFirstName" header="KYC Officer"></Column>
                        <Column
                            field="reviewStartedTimeStamp"
                            header="Review Started"
                            body={dateBodyTemplate("reviewStartedTimeStamp")}
                            filterMatchModeOptions={numberFilterMatchModeOptions}
                            dataType="date"
                            filterElement={dateFilterTemplate}
                        ></Column>
                        <Column
                            header="Action"
                            body={this.continueReviewTemplate}
                            headerStyle={{ width: "8em", textAlign: "center" }}
                            bodyStyle={{ textAlign: "center", overflow: "visible" }}
                        />
                    </ExtendedDataTable>
                </AppCard>
            </PageLayout>
        );
    }
}
